import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle } from "lucide-react";
import { slidesImport } from "@/lib/adminApi";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Bulk-import van producten via CSV/Excel. Eén rij = één product; stoffen en
// profielen worden herkend op naam en automatisch aangemaakt/gekoppeld.
const TEMPLATE_HEADERS = [
  "cnk", "product_naam", "merk", "vorm", "product_werkzame_stof", "afbeelding_url",
  "stof_naam", "werking", "bijwerkingen_vaak", "bijwerkingen_ernstig", "algemene_waarschuwingen",
  "profiel_naam", "sterkte", "indicatie", "gebruik", "dosering", "praktische_info",
];
const TEMPLATE_EXAMPLE = [
  "1234567", "Voorbeeldproduct 400 mg (VERWIJDER DEZE RIJ)", "Voorbeeldmerk", "Tablet",
  "Ibuprofen 400 mg", "", "Ibuprofen",
  "Werkt pijnstillend, koortswerend en ontstekingsremmend",
  "Maagklachten, buikpijn", "Maagbloedingen: zwarte stoelgang, bloed braken",
  "Niet gebruiken bij maagzweren\n\nNiet tijdens het laatste trimester van de zwangerschap",
  "Ibuprofen 400 mg tablet", "400 mg", "Verlicht pijn en koorts",
  "Innemen met een glas water", "1 tablet, maximaal 3 keer per dag", "Bewaar op kamertemperatuur",
];

const KOPPEL_HEADERS = ["cnk", "profiel_naam", "stof_naam", "toedieningsvorm"];
const KOPPEL_EXAMPLE = [
  "1234567", "Ibuprofen 400 mg tablet (VOORBEELD - VERWIJDER)", "", "",
];

const downloadTemplate = (mode) => {
  const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
  const [headers, example, naam] = mode === "koppelingen"
    ? [KOPPEL_HEADERS, KOPPEL_EXAMPLE, "aponiqo-koppelingen-sjabloon.csv"]
    : [TEMPLATE_HEADERS, TEMPLATE_EXAMPLE, "aponiqo-import-sjabloon.csv"];
  const csv = [headers.map(esc).join(";"), example.map(esc).join(";")].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = naam;
  a.click();
  URL.revokeObjectURL(a.href);
};

const Stat = ({ label, value, tone = "text-[#0a2540]" }) => (
  <div className="rounded-lg border border-slate-200 px-3 py-2 text-center">
    <p className={`text-lg font-bold ${tone}`}>{value}</p>
    <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
  </div>
);

const Report = ({ data, mode }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="import-report">
      <Stat label="Rijen" value={data.rijen} />
      {mode === "koppelingen" ? (
        <Stat label="Herkoppeld" value={data.herkoppeld ?? 0} tone="text-[#0a5a3c]" />
      ) : (
        <>
          <Stat label="Gelukt" value={data.ok} tone="text-[#0a5a3c]" />
          <Stat label="Nieuwe producten" value={data.producten_nieuw} />
          <Stat label="Bijgewerkte producten" value={data.producten_bijgewerkt} />
          <Stat label="Nieuwe stoffen" value={data.stoffen_nieuw} />
          <Stat label="Nieuwe profielen" value={data.profielen_nieuw} />
          <Stat label="Inhoudsregels" value={data.inhoud_rijen} />
        </>
      )}
      <Stat label="Fouten" value={data.fouten.length} tone={data.fouten.length ? "text-rose-600" : "text-[#0a2540]"} />
    </div>
    {data.fouten.length > 0 && (
      <div className="max-h-40 overflow-y-auto rounded-lg border border-rose-200 bg-rose-50 p-3" data-testid="import-errors">
        {data.fouten.map((f, i) => (
          <p key={i} className="flex items-start gap-2 text-xs text-rose-700">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            <span><strong>Rij {f.rij}:</strong> {f.fout}</span>
          </p>
        ))}
      </div>
    )}
  </div>
);

const ImportDialog = ({ open, onOpenChange, onDone }) => {
  const [mode, setMode] = useState("producten");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) { setFile(null); setPreview(null); setResult(null); setBusy(false); setMode("producten"); }
  }, [open]);

  const analyze = async (f) => {
    if (!f) return;
    setFile(f);
    setBusy(true);
    setPreview(null);
    setResult(null);
    try {
      setPreview(await slidesImport(f, true, mode));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kon bestand niet analyseren");
      setFile(null);
    }
    setBusy(false);
  };

  const doImport = async () => {
    setBusy(true);
    try {
      const rep = await slidesImport(file, false, mode);
      setResult(rep);
      setPreview(null);
      toast.success(mode === "koppelingen"
        ? `${rep.herkoppeld ?? 0} koppelingen bijgewerkt`
        : `${rep.ok} rijen geïmporteerd`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Importeren mislukt");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle>Bulk import</DialogTitle>
          <DialogDescription className="sr-only">Upload een CSV- of Excel-bestand voor producten of CNK-koppelingen</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* importtype */}
          <div className="flex gap-2" data-testid="import-mode">
            {[["producten", "Producten importeren"], ["koppelingen", "CNK-koppelingen"]].map(([m, label]) => (
              <button key={m} type="button" onClick={() => { setMode(m); setFile(null); setPreview(null); setResult(null); }}
                data-testid={`import-mode-${m}`}
                className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  mode === m ? "bg-[#0a2540] text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
                {label}
              </button>
            ))}
          </div>

          <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-600">
            <p className="font-medium text-[#0a2540]">Zo werkt het</p>
            {mode === "producten" ? (
              <ul className="mt-1.5 list-inside list-disc space-y-1 text-xs leading-relaxed">
                <li>Download het sjabloon en vul één rij per product in (Excel of CSV).</li>
                <li>Werkzame stoffen en inhoudsprofielen worden automatisch herkend op naam of nieuw aangemaakt.</li>
                <li>Bestaande producten (zelfde CNK) worden bijgewerkt, nooit verdubbeld.</li>
                <li>Je krijgt eerst een controle-rapport — er wordt pas geschreven als je bevestigt.</li>
              </ul>
            ) : (
              <ul className="mt-1.5 list-inside list-disc space-y-1 text-xs leading-relaxed">
                <li>Download het koppel-sjabloon: één rij per CNK.</li>
                <li>Koppel via <strong>profiel_naam</strong> óf via <strong>stof_naam + toedieningsvorm</strong>.</li>
                <li>Enkel bestaande CNK's worden bijgewerkt — er worden nooit producten aangemaakt.</li>
                <li>Onbekende CNK's of profielen verschijnen als fout in het rapport, zonder iets te wijzigen.</li>
              </ul>
            )}
            <button onClick={() => downloadTemplate(mode)} data-testid="import-template-btn"
              className="mt-3 flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-[#0a2540] hover:bg-slate-50">
              <Download className="h-3.5 w-3.5" /> Download sjabloon (CSV)
            </button>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Bestand (.csv of .xlsx)</label>
            <input type="file" accept=".csv,.xlsx" data-testid="import-file-input"
              onChange={(e) => analyze(e.target.files?.[0] || null)}
              className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-[#0a5a3c]/10 file:px-3 file:py-2 file:text-sm file:font-medium file:text-[#0a5a3c]" />
          </div>

          {busy && (
            <p className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Bezig met verwerken…
            </p>
          )}

          {preview && !busy && (
            <div className="space-y-3" data-testid="import-preview">
              <p className="flex items-center gap-2 text-sm font-medium text-[#0a2540]">
                <FileSpreadsheet className="h-4 w-4" /> Controle-rapport (nog niets opgeslagen)
              </p>
              <Report data={preview} mode={mode} />
              <button onClick={doImport} disabled={(mode === "koppelingen" ? preview.herkoppeld : preview.ok) === 0} data-testid="import-confirm-btn"
                className="flex items-center gap-2 rounded-lg bg-[#0a2540] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#0a1f38] disabled:opacity-50">
                <Upload className="h-4 w-4" />
                {mode === "koppelingen"
                  ? `Koppel ${preview.herkoppeld ?? 0} CNK's definitief`
                  : `Importeer ${preview.ok} rijen definitief`}
              </button>
            </div>
          )}

          {result && !busy && (
            <div className="space-y-3" data-testid="import-result">
              <p className="flex items-center gap-2 text-sm font-medium text-[#0a5a3c]">
                <CheckCircle2 className="h-4 w-4" /> {mode === "koppelingen" ? "Koppelen voltooid" : "Import voltooid"}
              </p>
              <Report data={result} mode={mode} />
              <button onClick={() => onOpenChange(false)} data-testid="import-close-btn"
                className="rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
                Sluiten
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImportDialog;
